/** Configuration state of a machine's parts. */
import type { JsonValue } from '@protobuf-ts/runtime';
import { deleteIn, getIn, setIn } from '@thi.ng/paths';
import { isEqual } from 'lodash-es';
import type { ReadonlyDeep } from 'type-fest';

import type {
  APIRobotPartConfigOnly,
  UpdateMachinePartRequest,
  UpdateMachinePartsRequest,
} from '$lib/api/robots';
import { assertExists } from '$lib/assert';
import { areObjectsUnique } from '$lib/compare-objects';
import {
  ConfigErrorTypes,
  type ConfigSemanticError,
  type ConfigSyntaxError,
  type ReadonlyConfigErrors,
} from '$lib/parse-config';

const tryPrettyJson = (json: string): string => {
  try {
    return JSON.stringify(JSON.parse(json), undefined, 2);
  } catch {
    return json;
  }
};

import * as ConfigItem from './config-item';
import * as Schemas from './config-schemas';
import { getRawPartConfig, semanticallyParseRawPartConfig } from './raw-config';
import * as RawResources from './raw-resources';

/** The configuration of all parts on a robot. */
export interface MachineConfig {
  mainID: ConfigItem.PartID | undefined;
  subIDs: ConfigItem.PartID[];
  parts: Record<ConfigItem.PartID, MaybeMachineConfigPart>;
}

type MaybeMachineConfigPart = MachineConfigPart | InvalidMachineConfigPart;
type InvalidMachineConfigPart =
  | InvalidSemanticsMachineConfigPart
  | InvalidSyntaxMachineConfigPart;

interface MachineConfigPart
  extends ConfigItem.PartBase,
    ConfigItem.ResourceParent {
  fragmentMods: Record<
    ConfigItem.FragmentID,
    Record<ConfigItem.FragmentPrefix, ConfigItem.ReadonlyFragmentMod>
  >;
  otherProps: Record<string, unknown>;
  rawJsonText?: string;
}

interface InvalidSemanticsMachineConfigPart {
  id: ConfigItem.PartID;
  name: string;
  config: JsonValue;
  errorType: typeof ConfigErrorTypes.SEMANTIC;
  errors: ConfigSemanticError[];
}

interface InvalidSyntaxMachineConfigPart {
  id: ConfigItem.PartID;
  name: string;
  config: string;
  errorType: typeof ConfigErrorTypes.SYNTAX;
  errors: ConfigSyntaxError[];
}
export type ReadonlyInvalidSyntaxMachineConfigPart =
  ReadonlyDeep<InvalidSyntaxMachineConfigPart>;

export const isSemanticErrors = (
  errors: ReadonlyConfigErrors | undefined
): errors is readonly ConfigSemanticError[] =>
  errors?.every((error) => error.type === ConfigErrorTypes.SEMANTIC) ?? false;

export const isSyntaxErrors = (
  errors: ReadonlyConfigErrors | undefined
): errors is readonly ConfigSyntaxError[] =>
  errors?.every((error) => error.type === ConfigErrorTypes.SYNTAX) ?? false;

export type ReadonlyMachineConfig = ReadonlyDeep<MachineConfig>;

export type ReadonlyMaybeMachineConfigPart =
  ReadonlyDeep<MaybeMachineConfigPart>;
export type ReadonlyMachineConfigPart = ReadonlyDeep<MachineConfigPart>;
export type ReadonlyInvalidMachineConfigPart =
  ReadonlyDeep<InvalidMachineConfigPart>;

export const isMachineConfigPart = (
  part: ReadonlyMaybeMachineConfigPart | undefined
): part is ReadonlyMachineConfigPart => {
  return part !== undefined && !('errorType' in part);
};

export const getMachineConfigPart = (
  config: ReadonlyMachineConfig,
  partID: string
): ReadonlyMachineConfigPart | undefined => {
  const part = getIn(config, ['parts', partID]);
  if (isMachineConfigPart(part)) {
    return part;
  }
  return undefined;
};

export const isInvalidMachineConfigPart = (
  part: ReadonlyMaybeMachineConfigPart
): part is ReadonlyInvalidMachineConfigPart => {
  return 'errorType' in part;
};

export const isInvalidSyntaxMachineConfigPart = (
  part: ReadonlyMaybeMachineConfigPart
): part is ReadonlyInvalidSyntaxMachineConfigPart => {
  return (
    isInvalidMachineConfigPart(part) &&
    part.errorType === ConfigErrorTypes.SYNTAX
  );
};

/** Get a default, empty robot configuration. */
export const getEmptyConfig = (): ReadonlyMachineConfig => ({
  mainID: undefined,
  subIDs: [],
  parts: {},
});

interface PartListToConfigParams {
  apiParts: APIRobotPartConfigOnly[] | undefined;
  existingConfig: ReadonlyMachineConfig;
  replace: boolean;
}
/**
 * Given a set of parts from the API, create an updated config.
 *
 * Parts that exist in the API response will be added to the config.
 * Parts that do not exist in the API response will be removed from the config.
 * If `replace` is true, parts that exist in both will be replaced from the API.
 * Existing parts will be left untouched otherwise.
 *
 * @param apiParts The API response's parts list.
 * @param existingConfig The UI's current understanding of the robot config.
 * @param replace Replace any parts that already exist in `existingConfig`
 *   with the config from `apiParts`.
 *
 * @returns A new copy of the config, or `existingConfig` if no updates needed.
 */
export const partListToConfig = (
  params: PartListToConfigParams
): ReadonlyMachineConfig => {
  const { apiParts, existingConfig, replace } = params;
  const parts = apiParts ?? [];
  const mainID: ConfigItem.PartID | undefined = parts.find(
    ({ mainPart }) => mainPart
  )?.id;
  const subIDs: ConfigItem.PartID[] = parts.flatMap(({ id, mainPart }) =>
    mainPart ? [] : id
  );
  const apiPartIDs = new Set(parts.map(({ id }) => id));
  const existingPartIDs = new Set(
    existingConfig.mainID
      ? [existingConfig.mainID, ...existingConfig.subIDs]
      : []
  );
  const partsIDsToRemove = [...existingPartIDs].filter(
    (id) => !apiPartIDs.has(id)
  );
  const partsToSet = parts.filter(
    ({ id }) => replace || !existingPartIDs.has(id)
  );

  if (
    existingConfig.mainID === mainID &&
    partsIDsToRemove.length === 0 &&
    partsToSet.length === 0
  ) {
    return existingConfig;
  }

  let result = existingConfig;

  for (const id of partsIDsToRemove) {
    result = deleteIn(existingConfig, ['parts', id]);
  }

  for (const { id, name, robotConfig, robotConfigJson } of partsToSet) {
    const parseResult = semanticallyParseRawPartConfig(robotConfig);

    // Preserve rawJsonText on refetch; only set from robotConfigJson on initial load.
    const existingPart = existingConfig.parts[id];
    const existingRawJsonText = isMachineConfigPart(existingPart)
      ? existingPart.rawJsonText
      : undefined;

    const prettyRobotConfigJson = robotConfigJson
      ? tryPrettyJson(robotConfigJson)
      : undefined;

    result = parseResult.success
      ? setRawPart(result, {
          id,
          name,
          partConfig: parseResult.data,
          rawJsonText: existingRawJsonText ?? prettyRobotConfigJson,
        })
      : setInvalidRawPart(result, {
          id,
          name,
          errorType: ConfigErrorTypes.SEMANTIC,
          error: parseResult.errors,
          partConfig: robotConfig as Record<string, unknown>,
        });
  }

  return { ...result, mainID, subIDs };
};

/** Given a normalized config, return a list of part updates.
 *
 * If any potential part updates match what's already in `apiParts`,
 * those updates will be skipped.
 */
export const configToPartUpdates = (
  config: ReadonlyMachineConfig,
  machineID: string | undefined,
  apiParts: APIRobotPartConfigOnly[] | undefined,
  overrideLastUpdated = false
): UpdateMachinePartsRequest | undefined => {
  if (machineID === undefined || config.mainID === undefined) {
    return undefined;
  }

  const apiPartsByID = Object.fromEntries(
    (apiParts ?? []).map((apiPart) => [apiPart.id, apiPart])
  );
  const partIDs = [config.mainID, ...config.subIDs];
  const updates: UpdateMachinePartRequest[] = partIDs.flatMap((partID) => {
    const rawConfig = getRawPartConfig(config, partID);
    const apiPart = apiPartsByID[partID];
    const partName = config.parts[partID]?.name;
    const part = config.parts[partID];

    assertExists(partName, 'invalid machine config: part missing name');

    // Use rawJsonText as robotConfigJson if values match, to preserve order-only changes.
    let robotConfigJson: string | undefined;
    const mPart = isMachineConfigPart(part) ? part : undefined;
    if (mPart?.rawJsonText) {
      try {
        if (isEqual(JSON.parse(mPart.rawJsonText), rawConfig)) {
          robotConfigJson = mPart.rawJsonText;
        }
      } catch {
        // ignore parse errors, fall through to JSON.stringify
      }
    }

    const update: UpdateMachinePartRequest = {
      id: partID,
      name: partName,
      robotConfig: rawConfig,
      robotConfigJson,
      lastUpdated: overrideLastUpdated ? undefined : apiPart?.lastUpdated,
    };

    const valuesUnique = areObjectsUnique(
      // strip lastUpdated from the uniqueness check
      { id: update.id, name: update.name, robotConfig: update.robotConfig },
      apiPart
        ? {
            id: apiPart.id,
            name: apiPart.name,
            robotConfig: apiPart.robotConfig,
          }
        : undefined
    );
    // Also save when only field order changed.
    const orderChanged =
      robotConfigJson !== undefined &&
      robotConfigJson !== apiPart?.robotConfigJson;

    return valuesUnique || orderChanged ? update : [];
  });

  return { machineID, updates };
};

interface BaseRawPart {
  id: string;
  name?: string;
}

export interface RawPart extends BaseRawPart {
  partConfig: Schemas.RawPartConfig;
  rawJsonText?: string;
}

export type InvalidRawPart = InvalidSemanticsRawPart | InvalidSyntaxRawPart;

interface InvalidSemanticsRawPart extends BaseRawPart {
  errorType: typeof ConfigErrorTypes.SEMANTIC;
  error: ConfigSemanticError[];
  partConfig: unknown;
}

interface InvalidSyntaxRawPart extends BaseRawPart {
  errorType: typeof ConfigErrorTypes.SYNTAX;
  error: ConfigSyntaxError[];
  partConfig: string;
}

/** Given the existing config, return a new config with a part replaced. */
export const setRawPart = (
  config: ReadonlyMachineConfig,
  rawPart: RawPart
): ReadonlyMachineConfig => {
  const { id, name: nextName, partConfig, rawJsonText } = rawPart;
  const existingPart = config.parts[id];
  const name = nextName ?? existingPart?.name;

  if (!name) {
    return config;
  }

  const {
    agent,
    network,
    maintenance,
    components = [],
    services = [],
    modules = [],
    remotes = [],
    processes = [],
    packages = [],
    fragments = [],
    webhooks = [],
    triggers = [],
    jobs = [],
    fragment_mods: fragmentMods = [],
    ...otherProps
  } = partConfig;

  const part: MachineConfigPart = {
    id,
    name,
    otherProps,
    rawJsonText,
    fragmentMods: RawResources.fromRawFragmentMods(fragmentMods),
    ...RawResources.fromRawResourceParent(
      {
        agent,
        network,
        maintenance,
        components,
        services,
        modules,
        remotes,
        processes,
        packages,
        fragments,
        webhooks,
        triggers,
        jobs,
        fragmentIDs: fragments,
      },
      id
    ),
  };

  return setIn(config, ['parts', part.id], part);
};

export const setInvalidRawPart = (
  config: ReadonlyMachineConfig,
  rawPart: InvalidRawPart
): ReadonlyMachineConfig => {
  const { id, name: nextName, errorType } = rawPart;
  const existingPart = config.parts[id];
  const name = nextName ?? existingPart?.name;

  if (!name) {
    return config;
  }

  const base = { id, name };
  const part: InvalidMachineConfigPart =
    errorType === ConfigErrorTypes.SEMANTIC
      ? {
          ...base,
          errorType: ConfigErrorTypes.SEMANTIC,
          errors: rawPart.error,
          config: rawPart.partConfig as JsonValue,
        }
      : {
          ...base,
          errorType: ConfigErrorTypes.SYNTAX,
          errors: rawPart.error,
          config: rawPart.partConfig,
        };

  return setIn(config, ['parts', part.id], part);
};
